import { useRef, useState } from "react";
import { useForm, usePage } from "@inertiajs/react";
import { useTranslation } from "react-i18next";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import {
  NativeSelect,
  NativeSelectOption,
} from "@/components/ui/native-select";
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetDescription,
} from "@/components/ui/sheet";
import { FormErrors } from "@/components/forms/form-errors";
import { locationJobAssignmentPath } from "@/routes";

export default function JobAssignment() {
  const { auth, job_context: job, errors } = usePage().props;
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const trigger = useRef<HTMLButtonElement>(null);
  const form = useForm({
    assignment: {
      revision: job?.revision || "",
      assignee_id: job?.assignee_id || "",
      helper_ids: job?.helper_ids || [],
    },
    return_section: "schedule",
  });
  if (!job || !auth.location) return null;
  if (!job.can_assign)
    return job.pending_reschedule ? (
      <p role="status" className="rounded-lg border p-4 text-sm">
        {t("workspace.assignment_pending")}
      </p>
    ) : null;
  return (
    <>
      <FormErrors errors={errors} />
      <Button
        ref={trigger}
        variant="outline"
        onClick={() => {
          form.setData({
            assignment: {
              revision: job.revision,
              assignee_id: job.assignee_id,
              helper_ids: job.helper_ids,
            },
            return_section: "schedule",
          });
          form.clearErrors();
          setOpen(true);
        }}
      >
        {t("workspace.assign")}
      </Button>
      <Sheet open={open} onOpenChange={setOpen}>
        <SheetContent
          finalFocus={trigger}
          className="w-full! overflow-y-auto sm:max-w-lg!"
        >
          <SheetHeader>
            <SheetTitle>{t("workspace.assign")}</SheetTitle>
            <SheetDescription>
              {t("workspace.assignment_help")}
            </SheetDescription>
          </SheetHeader>
          <form
            className="space-y-5 px-4 pb-4"
            onSubmit={(event) => {
              event.preventDefault();
              form.patch(locationJobAssignmentPath(auth.location!.id, job.id), {
                preserveScroll: true,
                onSuccess: () => setOpen(false),
              });
            }}
          >
            <FormErrors
              errors={form.errors}
              targets={{
                assignee: "assignment-responsible",
                assignee_id: "assignment-responsible",
              }}
            />
            <div className="space-y-2">
              <Label htmlFor="assignment-responsible">
                {t("jobs.assignee_id")}
              </Label>
              <NativeSelect
                id="assignment-responsible"
                required
                className="w-full"
                value={form.data.assignment.assignee_id}
                onChange={(event) =>
                  form.setData("assignment", {
                    ...form.data.assignment,
                    assignee_id: event.target.value,
                    helper_ids: form.data.assignment.helper_ids.filter(
                      (id) => id !== event.target.value,
                    ),
                  })
                }
              >
                {job.staff.map((person) => (
                  <NativeSelectOption key={person.id} value={person.id}>
                    {person.name}
                  </NativeSelectOption>
                ))}
              </NativeSelect>
            </div>
            <fieldset className="space-y-2">
              <legend className="mb-2 font-medium">{t("jobs.helpers")}</legend>
              {job.staff
                .filter(
                  (person) => person.id !== form.data.assignment.assignee_id,
                )
                .map((person) => (
                  <label
                    key={person.id}
                    className="flex min-h-11 items-center gap-3"
                  >
                    <input
                      type="checkbox"
                      checked={form.data.assignment.helper_ids.includes(
                        person.id,
                      )}
                      onChange={(event) =>
                        form.setData("assignment", {
                          ...form.data.assignment,
                          helper_ids: event.target.checked
                            ? [...form.data.assignment.helper_ids, person.id]
                            : form.data.assignment.helper_ids.filter(
                                (id) => id !== person.id,
                              ),
                        })
                      }
                    />
                    {person.name}
                  </label>
                ))}
            </fieldset>
            <div className="flex flex-wrap gap-3">
              <Button type="submit" disabled={form.processing}>
                {t("common.save")}
              </Button>
              <Button
                variant="outline"
                type="button"
                onClick={() => setOpen(false)}
              >
                {t("common.cancel")}
              </Button>
            </div>
          </form>
        </SheetContent>
      </Sheet>
    </>
  );
}
